import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { STORAGE_KEYS, TWEAK_DEFAULTS } from '../config/env';

const STORAGE_KEY = STORAGE_KEYS.tweaks;

const ThemeContext = createContext(null);

// Reads and validates the saved dark-mode preference, including the previous Tweaks object shape.
function readStoredDarkMode() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return TWEAK_DEFAULTS.dark;

    const stored = JSON.parse(raw);
    if (typeof stored === 'boolean') return stored;
    if (stored && typeof stored.dark === 'boolean') return stored.dark;
  } catch (e) { /* use the product default */ }

  return TWEAK_DEFAULTS.dark;
}

// Provides the persisted dark-mode state used by both CSS and MUI themes.
export function ThemeProvider({ children }) {
  const [dark, setDark] = useState(readStoredDarkMode);

  // Persist only the supported theme preference and discard obsolete Tweaks fields.
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ dark })); } catch (e) { /* ignore */ }
  }, [dark]);

  // Toggles the single display preference exposed to users.
  const toggleDarkMode = useCallback(() => {
    setDark(currentDark => !currentDark);
  }, []);

  const value = useMemo(() => ({
    dark,
    toggleDarkMode,
    appClass: `app ${dark ? 'dark' : 'light'}`,
  }), [dark, toggleDarkMode]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

// Returns the current dark-mode state and action within the application theme boundary.
export function useThemeContext() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useThemeContext must be used within ThemeProvider');
  return ctx;
}
